import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Check,
  ArrowLeft,
  Zap,
  Rocket,
  Crown,
  Loader2,
  BarChart3,
  Brain,
  FileText,
  Layers,
  AlertCircle,
  X,
  ShoppingCart,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://your-railway-backend-url.railway.app'}/api`;
const PLAN_ORDER = ["free", "pro", "power"];
const PLANS_DISPLAY = { free: "Free", pro: "Pro", power: "Power" };

const PLAN_META = {
  free: {
    icon: <Zap className="w-5 h-5" />,
    accent: "text-slate-400",
    accentBg: "bg-slate-400/10",
    border: "border-border",
    cardBg: "bg-card",
    badge: null,
    tagline: "Explore at your own pace",
    ctaUp: "Get Started",
  },
  pro: {
    icon: <Rocket className="w-5 h-5" />,
    accent: "text-primary",
    accentBg: "bg-primary/10",
    border: "border-primary",
    cardBg: "bg-[hsl(222_20%_12%)]",
    badge: "Most Popular",
    tagline: "For serious practitioners",
    ctaUp: "Upgrade to Pro",
  },
  power: {
    icon: <Crown className="w-5 h-5" />,
    accent: "text-amber-400",
    accentBg: "bg-amber-400/10",
    border: "border-amber-400/60",
    cardBg: "bg-card",
    badge: null,
    tagline: "Maximum AI throughput",
    ctaUp: "Upgrade to Power",
  },
};

const INCLUDED_SECTIONS = [
  {
    title: "All DMAIC Phases",
    icon: <Layers className="w-5 h-5" />,
    items: [
      "Define, Measure, Analyze, Improve, Control",
      "Gate reviews & phase approvals",
      "Project closure workflow",
    ],
  },
  {
    title: "Full Statistical Engine",
    icon: <BarChart3 className="w-5 h-5" />,
    items: [
      "Hypothesis testing & regression",
      "Control charts & capability analysis",
      "Design of Experiments (DOE)",
    ],
  },
  {
    title: "AI Black Belt Assistant",
    icon: <Brain className="w-5 h-5" />,
    items: [
      "Root-cause analysis & suggestions",
      "Solution generation & FMEA",
      "Guided project creation",
    ],
  },
  {
    title: "Reporting & Exports",
    icon: <FileText className="w-5 h-5" />,
    items: [
      "Executive summaries & PDF export",
      "Portfolio intelligence dashboard",
      "Full project audit trail",
    ],
  },
];

export default function Pricing() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [packsLoading, setPacksLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPlans();
    if (token) {
      fetchCurrentPlan();
      fetchCreditPacks();
    }
    else setLoading(false);
  }, [token]);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API}/subscription/plans`);
      if (res.ok) setPlans((await res.json()).plans);
    } catch (err) {
      console.error("Failed to fetch plans:", err);
    }
  };

  const fetchCurrentPlan = async () => {
    try {
      const res = await fetch(`${API}/subscription/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCurrentPlan(await res.json());
    } catch (err) {
      console.error("Failed to fetch current plan:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditPacks = async () => {
    if (!token) return;
    setPacksLoading(true);
    try {
      const res = await fetch(`${API}/credits/packs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setCreditPacks(data.enabled ? data.packs || [] : []);
    } catch (err) {
      console.error("Failed to fetch credit packs:", err);
    } finally {
      setPacksLoading(false);
    }
  };

  /* ── upgrade via Stripe checkout ── */
  const handleUpgrade = async (planId) => {
    if (!token) { navigate("/register"); return; }
    setProcessingPlan(planId);
    try {
      const res = await fetch(`${API}/subscription/create-checkout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          tier: planId,
          success_url: `${window.location.origin}/subscription/success`,
          cancel_url: `${window.location.origin}/pricing`,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(typeof err.detail === "string" ? err.detail : err.detail?.message || "Checkout failed");
      }
      window.location.href = (await res.json()).checkout_url;
    } catch (err) {
      toast.error(err.message);
      setProcessingPlan(null);
    }
  };

  /* ── schedule downgrade ── */
  const handleDowngrade = async (planId) => {
    if (!token) return;
    setProcessingPlan(planId);
    try {
      const res = await fetch(`${API}/subscription/downgrade`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ tier: planId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Downgrade failed");
      toast.success(data.message);
      await fetchCurrentPlan();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessingPlan(null);
    }
  };

  /* ── cancel pending downgrade ── */
  const handleCancelDowngrade = async () => {
    try {
      const res = await fetch(`${API}/subscription/cancel-downgrade`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Cancel failed");
      toast.success(data.message);
      await fetchCurrentPlan();
    } catch (err) {
      toast.error(err.message);
    }
  };

  /* ── helpers ── */
  const isCurrent = (id) => currentPlan?.plan === id;
  const planIdx = (id) => PLAN_ORDER.indexOf(id);
  const currentIdx = currentPlan ? planIdx(currentPlan.plan) : -1;

  const btnConfig = (id) => {
    if (!currentPlan) return { label: id === "free" ? "Get Started" : PLAN_META[id]?.ctaUp, action: () => id === "free" ? navigate("/register") : handleUpgrade(id), variant: id === "pro" ? "default" : "secondary", disabled: false };
    if (isCurrent(id)) return { label: "Current Plan", action: () => {}, variant: "outline", disabled: true };
    if (planIdx(id) > currentIdx) return { label: PLAN_META[id]?.ctaUp, action: () => handleUpgrade(id), variant: id === "pro" ? "default" : "secondary", disabled: false };
    // downgrade
    const pending = currentPlan.pending_downgrade;
    if (pending && pending.target_plan === id) return { label: "Downgrade Scheduled", action: () => {}, variant: "outline", disabled: true };
    return { label: `Downgrade to ${PLANS_DISPLAY[id] || capitalize(id)}`, action: () => handleDowngrade(id), variant: "outline", disabled: false };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pendingDg = currentPlan?.pending_downgrade;

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      {/* ── Header ── */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(token ? "/dashboard" : "/")}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="pricing-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {!token && (
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => navigate("/login")}>Sign In</Button>
              <Button onClick={() => navigate("/register")}>Get Started</Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        {/* ── Pending downgrade banner ── */}
        {pendingDg && (
          <div className="mb-8 bg-amber-400/10 border border-amber-400/30 rounded-xl p-4 flex items-center justify-between" data-testid="pending-downgrade-banner">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
              <p className="text-sm">
                Your plan will downgrade to <span className="font-semibold">{pendingDg.target_plan_name}</span> on{" "}
                <span className="font-semibold">{new Date(pendingDg.effective_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>.
                You'll keep full access until then.
              </p>
            </div>
            <button onClick={handleCancelDowngrade} className="flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300 transition-colors shrink-0 ml-4" data-testid="cancel-downgrade-btn">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        )}

        {/* ── Hero ── */}
        <div className="text-center mb-16">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-5">
            Simple, usage-based pricing
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-lg mx-auto leading-relaxed">
            Every feature is included on every plan. Pick the usage limits that match your workflow.
          </p>
        </div>

        {/* ── Plan Cards ── */}
        <div className="grid md:grid-cols-3 gap-5 lg:gap-7 mb-24">
          {plans.map((plan) => {
            const meta = PLAN_META[plan.id] || PLAN_META.free;
            const current = isCurrent(plan.id);
            const btn = btnConfig(plan.id);
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 ${meta.border} ${meta.cardBg} p-7 flex flex-col transition-all duration-200 hover:translate-y-[-2px] hover:shadow-xl ${
                  current ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {meta.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3.5 py-1 rounded-full whitespace-nowrap">
                    {meta.badge}
                  </span>
                )}

                <div className="flex items-center gap-2.5 mb-1">
                  <span className={`p-1.5 rounded-lg ${meta.accentBg} ${meta.accent}`}>{meta.icon}</span>
                  <h3 className="font-heading text-xl font-semibold">{plan.name}</h3>
                </div>
                <p className="text-muted-foreground text-sm mb-6">{meta.tagline}</p>

                <div className="mb-7">
                  {plan.price_monthly > 0 ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold tracking-tight">${plan.price_monthly}</span>
                      <span className="text-muted-foreground text-sm">/month</span>
                    </div>
                  ) : (
                    <span className="text-4xl font-bold tracking-tight">Free</span>
                  )}
                </div>

                <div className="space-y-3.5 mb-8 flex-grow">
                  <LimitRow accent={meta.accent} text={plan.max_projects === null ? "Unlimited projects" : `${plan.max_projects} active project`} />
                  <LimitRow accent={meta.accent} text={`${plan.monthly_ai_credits.toLocaleString()} AI credits / month`} />
                  <LimitRow accent={meta.accent} text={`${capitalize(plan.ai_priority)} priority AI`} />
                </div>

                <Button
                  className="w-full h-11 text-sm font-semibold"
                  variant={btn.variant}
                  disabled={btn.disabled || !!processingPlan}
                  onClick={btn.action}
                  data-testid={`plan-btn-${plan.id}`}
                >
                  {processingPlan === plan.id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {processingPlan === plan.id ? "Processing..." : btn.label}
                </Button>
              </div>
            );
          })}
        </div>

        <section className="mb-24" data-testid="pricing-credit-packs">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">Need a one-time credit top-up?</h2>
              <p className="text-muted-foreground text-sm sm:text-base max-w-2xl">
                Buy extra AI credits without changing your monthly plan. Purchased credits persist across billing cycles and are used after your monthly allocation.
              </p>
            </div>
            <Button
              onClick={() => navigate(token ? "/buy-credits" : "/login")}
              className="gap-2 shrink-0"
              variant="outline"
              data-testid="pricing-buy-credits-btn"
            >
              <ShoppingCart className="w-4 h-4" />
              {token ? "Buy Credits" : "Sign In to Buy Credits"}
            </Button>
          </div>

          {token ? (
            packsLoading ? (
              <div className="grid md:grid-cols-3 gap-5 lg:gap-7">
                {[0, 1, 2].map((index) => (
                  <div key={index} className="rounded-2xl border border-border bg-card p-6 animate-pulse">
                    <div className="h-4 w-24 rounded bg-muted mb-4" />
                    <div className="h-8 w-20 rounded bg-muted mb-3" />
                    <div className="h-4 w-32 rounded bg-muted mb-6" />
                    <div className="h-10 w-full rounded bg-muted" />
                  </div>
                ))}
              </div>
            ) : creditPacks.length > 0 ? (
              <div className="grid md:grid-cols-3 gap-5 lg:gap-7">
                {creditPacks.map((pack, index) => (
                  <div
                    key={pack.id}
                    className={`rounded-2xl border p-6 ${index === 1 ? "border-primary bg-primary/5" : "border-border bg-card"}`}
                    data-testid={`pricing-pack-${pack.id}`}
                  >
                    <div className="text-sm font-medium text-muted-foreground mb-2">{pack.label}</div>
                    <div className="text-3xl font-bold tracking-tight mb-1">${pack.price_usd}</div>
                    <div className="text-sm text-muted-foreground mb-5">{pack.credits.toLocaleString()} credits</div>
                    <div className="space-y-2 text-sm mb-6">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-primary" />
                        <span>${pack.price_per_credit}/credit</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-primary" />
                        <span>No expiration</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-primary" />
                        <span>Used after monthly credits</span>
                      </div>
                    </div>
                    <Button
                      className="w-full"
                      variant={index === 1 ? "default" : "secondary"}
                      onClick={() => navigate("/buy-credits")}
                    >
                      Choose Pack
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground bg-card">
                Credit packs are temporarily unavailable for your account.
              </div>
            )
          ) : (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center bg-card">
              <p className="text-muted-foreground mb-4">
                Sign in to view available credit packs and complete a one-time purchase from inside the app.
              </p>
              <Button onClick={() => navigate("/login")} data-testid="pricing-credit-packs-login-btn">
                Sign In
              </Button>
            </div>
          )}
        </section>

        {/* ── Everything Included ── */}
        <section className="mb-24" data-testid="everything-included">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">Everything included</h2>
            <p className="text-muted-foreground text-sm sm:text-base max-w-md mx-auto">
              No feature gates, no add-ons. Every plan ships with the full BlackBelt AI toolkit.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {INCLUDED_SECTIONS.map((sec) => (
              <div key={sec.title} className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="p-1.5 rounded-lg bg-primary/10 text-primary">{sec.icon}</span>
                  <h3 className="font-semibold text-sm">{sec.title}</h3>
                </div>
                <ul className="space-y-2">
                  {sec.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary/70" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="max-w-2xl mx-auto">
          <h2 className="font-heading text-lg font-semibold text-center mb-8">Frequently asked questions</h2>
          <div className="space-y-4">
            <FaqItem q="What are AI credits?" a="Each AI-powered action consumes credits based on complexity: quick suggestions cost 1 credit, analyses cost 5, and report generation costs 15. Credits reset monthly on your billing date." />
            <FaqItem q="Can I switch plans anytime?" a="Yes. Upgrades take effect immediately with refreshed credits. Downgrades are scheduled for the end of your current billing cycle so you keep full access until then." />
            <FaqItem q="What happens when I run out of credits?" a="All features remain accessible — only AI-powered actions pause until your credits reset or you upgrade your plan." />
            <FaqItem q="Can I buy more credits?" a="Yes. Credit packs are available for one-time purchase. Purchased credits persist across billing cycles and are consumed after your monthly allocation." />
          </div>
        </section>
      </main>
    </div>
  );
}

function LimitRow({ accent, text }) {
  return (
    <div className="flex items-start gap-2.5">
      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${accent}`} />
      <span className="text-sm">{text}</span>
    </div>
  );
}

function FaqItem({ q, a }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="font-semibold text-sm mb-1.5">{q}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{a}</p>
    </div>
  );
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}
